import { useId, useMemo, useState } from 'react';
import type { ChatPrompt, ChatPromptOption } from '../api/chatClient';
import { AdaptiveCardBubble } from './AdaptiveCardBubble';

/**
 * Renders any server-issued {@link ChatPrompt} and returns the collected values.
 *
 * One component for every structured step — classification confirmation, eligibility device,
 * modality selection, callback details — replacing the per-feature cards each of which previously
 * needed its own renderer AND its own REST call to answer. Adding a new step is now a server-side
 * change as long as it reuses `choice` or `form`.
 *
 * `choice` and `form` render as native controls rather than Adaptive Cards on purpose: the Adaptive
 * Cards renderer emits labels as TextBlocks with no programmatic association to their input (see the
 * post-render accessibility pass in PreChatSurveyCard), and card-authored text would move
 * localization server-side into baked-in English.
 */
export function ChatPromptCard({
  prompt,
  disabled = false,
  onSubmit,
}: {
  prompt: ChatPrompt;
  disabled?: boolean;
  onSubmit: (values: Record<string, string | boolean>) => void;
}) {
  if (prompt.kind === 'adaptive-card') {
    return (
      <AdaptiveCardBubble
        card={prompt.card}
        disabled={disabled}
        onSubmit={(value) =>
          onSubmit(
            value && typeof value === 'object'
              ? (value as Record<string, string | boolean>)
              : {},
          )
        }
      />
    );
  }

  if (prompt.kind === 'form') {
    return <PromptForm prompt={prompt} disabled={disabled} onSubmit={onSubmit} />;
  }

  return <PromptChoices prompt={prompt} disabled={disabled} onSubmit={onSubmit} />;
}

function PromptChoices({
  prompt,
  disabled,
  onSubmit,
}: {
  prompt: ChatPrompt;
  disabled: boolean;
  onSubmit: (values: Record<string, string | boolean>) => void;
}) {
  const captionId = useId();
  const options = (prompt.options ?? []).filter((option) => !isNonAgentModalityOption(option));
  if (options.length === 0) return null;

  return (
    <div
      style={styles.options}
      role="group"
      aria-labelledby={prompt.text ? captionId : undefined}
      aria-label={prompt.text ? undefined : 'Choose an option'}
    >
      {prompt.text && <div id={captionId} style={styles.promptText}>{prompt.text}</div>}
      {options.map((option) => (
        <ChoiceOption
          key={option.id}
          option={option}
          disabled={disabled}
          onSubmit={onSubmit}
        />
      ))}
    </div>
  );
}

export function isNonAgentModalityOption(option: ChatPromptOption) {
  return option.group === 'non-agent' || !!option.redirectUrl;
}

export function ChatPromptNonAgentModalityLinks({
  prompt,
  disabled = false,
  onSubmit,
}: {
  prompt: ChatPrompt;
  disabled?: boolean;
  onSubmit: (values: Record<string, string | boolean>) => void;
}) {
  if (prompt.kind !== 'choice') return null;

  const nonAgentModalities = (prompt.options ?? []).filter(isNonAgentModalityOption);
  if (nonAgentModalities.length === 0) return null;

  return (
    <div style={styles.resources}>
      <div style={styles.resourceIntro}>Additional resources:</div>
      <ul style={styles.resourceList}>
        {nonAgentModalities.map((option) => (
          <ResourceLink
            key={option.id}
            option={option}
            disabled={disabled}
            onSubmit={onSubmit}
          />
        ))}
      </ul>
    </div>
  );
}

function ChoiceOption({
  option,
  disabled,
  onSubmit,
}: {
  option: ChatPromptOption;
  disabled: boolean;
  onSubmit: (values: Record<string, string | boolean>) => void;
}) {
  const available = option.isAvailable !== false;
  const isDisabled = disabled || !available;
  return (
    <button
      type="button"
      disabled={isDisabled}
      style={isDisabled ? { ...styles.option, ...styles.disabledOption } : styles.option}
      onClick={() => onSubmit({ optionId: option.id })}
    >
      <OptionBody option={option} />
    </button>
  );
}

function ResourceLink({
  option,
  disabled,
  onSubmit,
}: {
  option: ChatPromptOption;
  disabled: boolean;
  onSubmit: (values: Record<string, string | boolean>) => void;
}) {
  const available = option.isAvailable !== false;
  const redirectUrl = option.redirectUrl ?? undefined;
  const canOpen = available && !disabled && !!redirectUrl;
  const secondary = option.description
    ?? (available ? null : option.unavailableReason)
    ?? option.detail;

  return (
    <li style={styles.resourceItem}>
      {canOpen ? (
        <a
          href={redirectUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.resourceLink}
          onClick={() => {
            // Let the anchor's default action open the resource before submitting the prompt.
            // A synchronous submit can rerender and remove the anchor before navigation completes.
            window.setTimeout(() => onSubmit({ optionId: option.id }), 0);
          }}
        >
          {option.label}
        </a>
      ) : (
        <span aria-disabled="true" style={styles.disabledResourceLink}>
          {option.label}
        </span>
      )}
      {secondary && <span style={styles.resourceDetail}>{secondary}</span>}
    </li>
  );
}

function OptionBody({ option }: { option: ChatPromptOption }) {
  const isClosed = option.isAvailable === false && option.unavailableReason === 'OutOfBusinessHours';
  const showOperatingHours = !!option.detail
    && (isClosed || (option.group === 'agent' && option.isAvailable === false));
  const secondary = isClosed
    ? option.description
    : option.description
      ?? (option.isAvailable === false ? option.unavailableReason : null)
      ?? (showOperatingHours ? null : option.detail);
  return (
    <>
      <strong>{option.label}</strong>
      {secondary && <span style={styles.optionDetail}>{secondary}</span>}
      {isClosed && <span style={styles.closedStatus}>Currently closed</span>}
      {showOperatingHours && <span style={styles.operatingHours}>Operating hours: {option.detail}</span>}
    </>
  );
}

function PromptForm({
  prompt,
  disabled,
  onSubmit,
}: {
  prompt: ChatPrompt;
  disabled: boolean;
  onSubmit: (values: Record<string, string | boolean>) => void;
}) {
  const fields = useMemo(() => prompt.fields ?? [], [prompt.fields]);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((field) => [field.id, field.defaultValue ?? ''])));
  const [touched, setTouched] = useState(false);
  // Fields the customer has changed since the server rejected the previous value.
  const [editedFields, setEditedFields] = useState<ReadonlySet<string>>(() => new Set());

  const setValue = (id: string, value: string) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    setEditedFields((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  };

  const missing = fields.filter(
    (field) => field.isRequired && !values[field.id]?.trim());
  const canSubmit = !disabled && missing.length === 0;
  // A re-issued form carries the rejection both as its text and on the field; show it once, at the field.
  const introText = prompt.text && !fields.some((field) => field.errorMessage === prompt.text)
    ? prompt.text
    : null;

  return (
    <form
      style={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        setTouched(true);
        if (!canSubmit) return;
        onSubmit(values);
      }}
    >
      {introText && <div style={styles.promptText}>{introText}</div>}
      {fields.map((field) => {
        const requiredError = touched && field.isRequired && !values[field.id]?.trim();
        const serverError = editedFields.has(field.id) ? null : field.errorMessage;
        const errorText = requiredError ? `${field.label} is required` : serverError;
        const invalid = !!errorText;
        const errorId = `${field.id}-error`;
        return (
          <div key={field.id} style={styles.field}>
            {/* Explicit label/control association — the thing raw Adaptive Cards does not give us. */}
            <label htmlFor={field.id} style={styles.label}>
              {field.label}
              {field.isRequired && <span aria-hidden="true"> *</span>}
            </label>
            {field.type === 'choice' ? (
              <select
                id={field.id}
                style={invalid ? { ...styles.input, ...styles.inputInvalid } : styles.input}
                disabled={disabled}
                value={values[field.id] ?? ''}
                aria-required={field.isRequired || undefined}
                aria-invalid={invalid || undefined}
                aria-describedby={invalid ? errorId : undefined}
                onChange={(event) => setValue(field.id, event.target.value)}
              >
                {(field.options ?? []).map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            ) : (
              <input
                id={field.id}
                style={invalid ? { ...styles.input, ...styles.inputInvalid } : styles.input}
                disabled={disabled}
                type={field.type === 'tel' ? 'tel' : 'text'}
                inputMode={field.type === 'tel' ? 'tel' : undefined}
                autoComplete={field.type === 'tel' ? 'tel' : undefined}
                placeholder={field.placeholder ?? undefined}
                maxLength={field.maxLength ?? undefined}
                value={values[field.id] ?? ''}
                aria-required={field.isRequired || undefined}
                aria-invalid={invalid || undefined}
                aria-describedby={invalid ? errorId : undefined}
                onChange={(event) => setValue(field.id, event.target.value)}
              />
            )}
            {invalid && (
              <span id={errorId} role="alert" style={styles.error}>
                {errorText}
              </span>
            )}
          </div>
        );
      })}
      <button type="submit" disabled={disabled} style={styles.submit}>
        Submit
      </button>
    </form>
  );
}

const styles: Record<string, React.CSSProperties> = {
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 8,
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid var(--colorNeutralStroke1, #d1d1d1)',
    background: 'var(--colorNeutralBackground1, #fff)',
    color: 'inherit',
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
  },
  optionDetail: {
    fontSize: 12,
    opacity: 0.75,
  },
  disabledOption: {
    cursor: 'not-allowed',
    background: 'var(--colorNeutralBackgroundDisabled, #f0f0f0)',
    borderStyle: 'dashed',
  },
  closedStatus: {
    fontSize: 12,
    fontWeight: 600,
  },
  operatingHours: {
    fontSize: 12,
    whiteSpace: 'pre-line',
  },
  resources: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 8,
  },
  resourceIntro: {
    fontSize: 13,
  },
  resourceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    margin: 0,
    paddingLeft: 20,
  },
  resourceItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  resourceLink: {
    color: 'var(--colorBrandForegroundLink, #0f6cbd)',
    textDecoration: 'underline',
  },
  disabledResourceLink: {
    color: 'var(--colorNeutralForegroundDisabled, #bdbdbd)',
  },
  resourceDetail: {
    fontSize: 12,
    opacity: 0.75,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    border: '1px solid var(--colorNeutralStroke1, #d1d1d1)',
    background: 'var(--colorNeutralBackground1, #fff)',
  },
  promptText: {
    marginBottom: 2,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
  },
  input: {
    padding: '8px 10px',
    borderRadius: 6,
    border: '1px solid var(--colorNeutralStroke1, #d1d1d1)',
    font: 'inherit',
  },
  error: {
    fontSize: 12,
    color: 'var(--colorPaletteRedForeground1, #b10e1c)',
  },
  inputInvalid: {
    border: '1px solid var(--colorPaletteRedBorder2, #b10e1c)',
  },
  submit: {
    padding: '8px 14px',
    borderRadius: 6,
    border: 'none',
    background: 'var(--colorBrandBackground, #0f6cbd)',
    color: '#fff',
    cursor: 'pointer',
    font: 'inherit',
    alignSelf: 'flex-start',
  },
};
